import React, { useMemo } from "react";
import {
  Category,
  SubCategory,
  ChildCategory,
  BrandMapping,
  Brand,
} from "../../../../Redux/filters/types";

interface ChildCategorieFilterProps {
  superCategory?: string;
  parentCategories: Category[];
  subCategories: SubCategory[];
  childCategories: ChildCategory[];
  brandMappings: BrandMapping[];
  filters: {
    parentCategory: string;
    subCategory: string;
    childCategory: string;
    brandId: string;
    minPrice?: number;
    maxPrice?: number;
  };
  onFilterChange: (
    newFilters: Partial<ChildCategorieFilterProps["filters"]>,
  ) => void;
}

const ChildCategorieFilter: React.FC<ChildCategorieFilterProps> = ({
  superCategory,
  parentCategories,
  subCategories,
  childCategories,
  brandMappings,
  filters,
  onFilterChange,
}) => {
  const priceRanges = [
    { label: "Under ₹300", min: 0, max: 300 },
    { label: "₹300 - ₹500", min: 300, max: 500 },
    { label: "₹500 - ₹1000", min: 500, max: 1000 },
    { label: "₹1000 - ₹2000", min: 1000, max: 2000 },
    { label: "₹2000 - More", min: 2000, max: 1000000 },
  ];

  const filteredBrands = useMemo(() => {
    return brandMappings.filter((mapping) => {
      if (filters.parentCategory && mapping.category !== filters.parentCategory)
        return false;
      if (
        filters.subCategory &&
        mapping.subCategory &&
        mapping.subCategory !== filters.subCategory
      )
        return false;
      if (
        filters.childCategory &&
        mapping.childCategory &&
        mapping.childCategory !== filters.childCategory
      )
        return false;
      return true;
    });
  }, [brandMappings, filters]);

  return (
    <>
      <div className="display-max px-2 mt-3 text-nowrap">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <b className="text-dark fs-5">Filters</b>
          <button
            className="btn btn-link btn-sm text-info p-0 text-decoration-none"
            onClick={() =>
              onFilterChange({
                parentCategory: filters.parentCategory,
                subCategory: filters.subCategory,
                childCategory: "",
                brandId: "",
                minPrice: undefined,
                maxPrice: undefined,
              })
            }
          >
            Clear All
          </button>
        </div>

        {/* Category Hierarchy */}
        <div className="mb-4">
          <b className="text-dark text-sm">{superCategory || "Category"}</b>

          <div className="p-1 ms-2">
            {filters.parentCategory && (
              <div>
                <b className="text-dark text-sm d-block mb-1">
                  {filters.parentCategory}
                </b>

                {filters.subCategory && (
                  <div className="ms-2">
                    <button
                      className="btn btn-link p-0 text-sm fw-bold text-dark text-decoration-none mb-1"
                      onClick={() =>
                        onFilterChange({ subCategory: "", childCategory: "" })
                      }
                    >
                      <i className="bi bi-chevron-left me-1"></i>
                      {filters.subCategory}
                    </button>

                    <div className="ms-3 border-start ps-2">
                      {childCategories.map((child) => (
                        <div key={child._id}>
                          <button
                            className={`btn btn-link p-0 text-xs fw-normal text-start w-100 text-decoration-none ${filters.childCategory === child.name ? "text-info fw-bold" : "text-dark"}`}
                            onClick={() =>
                              onFilterChange({
                                childCategory:
                                  filters.childCategory === child.name
                                    ? ""
                                    : child.name,
                              })
                            }
                          >
                            {child.name}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <hr className="horizontal dark my-3" />

        {/* Price Filter */}
        <div className="mb-4">
          <b className="text-dark text-sm ms-2">Price</b>
          <div className="mt-2 ms-2">
            {priceRanges.map((range, index) => (
              <div className="form-check mb-1" key={index}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    filters.minPrice === range.min &&
                    filters.maxPrice === range.max
                  }
                  onChange={() =>
                    onFilterChange({
                      minPrice:
                        filters.minPrice === range.min ? undefined : range.min,
                      maxPrice:
                        filters.maxPrice === range.max ? undefined : range.max,
                    })
                  }
                  id={`child-price-${index}`}
                />
                <label
                  className="text-sm fw-normal text-dark ms-2"
                  htmlFor={`child-price-${index}`}
                >
                  {range.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <hr className="horizontal dark my-3" />

        {/* Brand Filter */}
        <div className="accordion" id="childFilterAccordion">
          <div className="accordion-item border-0 mb-3">
            <h2 className="accordion-header" id="childHeadingBrand">
              <button
                className="accordion-button px-2 py-2 fw-bold text-dark bg-transparent shadow-none"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#childCollapseBrand"
                aria-expanded="true"
                aria-controls="childCollapseBrand"
              >
                Brand
              </button>
            </h2>
            <div
              id="childCollapseBrand"
              className="accordion-collapse collapse show"
              aria-labelledby="childHeadingBrand"
            >
              <div className="accordion-body p-2 ms-2">
                {filteredBrands.length > 0 ? (
                  filteredBrands.map((mapping) => {
                    const brandId =
                      typeof mapping.brand === "object"
                        ? (mapping.brand as Brand)._id
                        : mapping.brand;
                    const brandName =
                      typeof mapping.brand === "object"
                        ? (mapping.brand as Brand).name
                        : mapping.brand;

                    return (
                      <div className="form-check mb-1" key={mapping._id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={filters.brandId === brandId}
                          onChange={() =>
                            onFilterChange({
                              brandId:
                                filters.brandId === brandId ? "" : brandId,
                            })
                          }
                          id={`child-brand-${mapping._id}`}
                        />
                        <label
                          className="text-sm fw-normal text-dark ms-2"
                          htmlFor={`child-brand-${mapping._id}`}
                        >
                          {brandName}
                        </label>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-muted">No brands found</p>
                )}
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingDiscount">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseDiscount"
                aria-expanded="false"
                aria-controls="collapseDiscount"
              >
                Discount
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseDiscount"
              className="accordion-collapse collapse"
              aria-labelledby="headingDiscount"
              data-bs-parent="#headingDiscount"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    30% or more
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    40% or more
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    50% or more
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    60% or more
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    70% or more
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingPattern">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapsePattern"
                aria-expanded="false"
                aria-controls="collapsePattern"
              >
                Pattern
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapsePattern"
              className="accordion-collapse collapse"
              aria-labelledby="headingPattern"
              data-bs-parent="#headingPattern"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Animal Print
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Blocked Print
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Checkered
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Color Block
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Digital Print
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Dyed
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Floral Print
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Graphic Print
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Polka Print
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingFabric">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseFabric"
                aria-expanded="false"
                aria-controls="collapseFabric"
              >
                Fabric
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseFabric"
              className="accordion-collapse collapse"
              aria-labelledby="headingFabric"
              data-bs-parent="#headingFabric"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Art Silk
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Brasso
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Chanderi
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Chiffon
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Cotton Blend
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Cotton Silk
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Georgette
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Lace
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Linen
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Pure Cotton
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Pure Silk
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Silk Blend
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Vicose Rayon
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingOccassion">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseOccassion"
                aria-expanded="false"
                aria-controls="collapseOccassion"
              >
                Occassion
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseOccassion"
              className="accordion-collapse collapse"
              aria-labelledby="headingOccassion"
              data-bs-parent="#headingOccassion"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Casual
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Party & Festive
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Wedding
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Wedding & Festive
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingColor">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseColor"
                aria-expanded="false"
                aria-controls="collapseColor"
              >
                Color
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseColor"
              className="accordion-collapse collapse"
              aria-labelledby="headingColor"
              data-bs-parent="#headingColor"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Beige
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Black
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Brown
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Cream
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Dark Blue
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Dark Green
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Gold
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Green
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Grey
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Light Blue
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Light Green
                  </label>
                </div>
                <div className="mb-1">
                  <a
                    href="."
                    className=" text-info fw-normal text-uppercase text-xs"
                  >
                    More{" "}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingSize">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseSize"
                aria-expanded="false"
                aria-controls="collapseSize"
              >
                Size
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseSize"
              className="accordion-collapse collapse"
              aria-labelledby="headingSize"
              data-bs-parent="#headingSize"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">XS</label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">S</label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">M</label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">L</label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">XL</label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    2XL
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    3XL
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    4XL
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    5XL
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Free Size
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingCustomRatings">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseCustomRatings"
                aria-expanded="false"
                aria-controls="collapseCustomRatings"
              >
                Customer Ratings
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseCustomRatings"
              className="accordion-collapse collapse"
              aria-labelledby="headingCustomRatings"
              data-bs-parent="#headingCustomRatings"
            >
              <div className="accordion-body">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    4<i className="bi bi-star-fill text-dark text-xs ms-1"></i>{" "}
                    & above
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    3<i className="bi bi-star-fill text-dark text-xs ms-1"></i>{" "}
                    & above
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingNewArrivals">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseNewArrivals"
                aria-expanded="false"
                aria-controls="collapseNewArrivals"
              >
                New Arrivals
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseNewArrivals"
              className="accordion-collapse collapse"
              aria-labelledby="headingNewArrivals"
              data-bs-parent="#headingNewArrivals"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    New Arrivals
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item border-bottom ms-2">
            <b className="accordion-header text-sm" id="headingAvailability">
              <button
                className="accordion-button fw-bold text-dark"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseAvailability"
                aria-expanded="false"
                aria-controls="collapseAvailability"
              >
                Availability
                <i className="collapse-close bi bi-chevron-down text-dark position-absolute end-2"></i>
                <i className="collapse-open bi bi-chevron-up text-dark position-absolute end-2"></i>
              </button>
            </b>
            <div
              id="collapseAvailability"
              className="accordion-collapse collapse"
              aria-labelledby="headingAvailability"
              data-bs-parent="#headingAvailability"
            >
              <div className="accordion-body mb-2">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="fcustomCheck1"
                  />
                  <label className="text-sm fw-normal text-dark ms-2">
                    Include Out of Stock
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChildCategorieFilter;
